import React, { useContext, useState, useMemo, useEffect, useCallback } from 'react';
import { AdsContext } from '../DataContexts/AdsContext';
import { UploadContext } from '../DataContexts/UploadContext';
import AdImagePicker from './AdImagePicker';
import { IMAGE_POOLS } from '../DataContexts/ImageLibraryContext';
import '../MachineManager/Blocklists.css';
import './Assign.css';
import './Design.css';

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const toBool = (v) => v === true || v === 'true';

const toDateInputValue = (v) => {
  if (v == null || v === '') return '';
  const s = String(v).trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : s.slice(0, 10);
};

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
const CREDIT_CONDITION_OPTIONS = [
  'NONE',
  'DISPLAY_ONLY_WITH_CREDITS',
  'DISPLAY_ONLY_WHEN_NO_CREDITS',
  'DISPLAY_ONLY_WHEN_BIJNA_BONUS',
];
const LOGO_REF_DEFAULT_OPTIONS = ['ad-icon-spinner.png', 'dj-logo-spinner.png'];

const Designer = ({ initialAdId = null }) => {
  const { advertisements, loading, error, createAdvertisement, updateAdvertisement } = useContext(AdsContext);
  const { uploadAdvertisementImage } = useContext(UploadContext);
  const [selectedAdId, setSelectedAdId] = useState(initialAdId);
  const [adSearch, setAdSearch] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [newAdName, setNewAdName] = useState('');
  const [creating, setCreating] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingNameValue, setEditingNameValue] = useState('');
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [layoutSaving, setLayoutSaving] = useState(false);
  const [layoutForm, setLayoutForm] = useState({
    displayLogo: true,
    displayLogoRef: '',
    displayLogoX: 0,
    displayLogoY: 0,
    displayMediaBar: false,
    displayMediaBarX: 0,
    displayMediaBarY: 0,
    displayErZijnNogCreditsCard: false,
    erZijnNogCreditsCardX: 0,
    erZijnNogCreditsCardY: 0,
    creditCondition: 'NONE',
    displayTimeInMilliseconds: 0,
    active: true,
    priority: 0,
    scheduledStartDate: '',
    startHourCondition: 0,
    startMinuteCondition: 0,
    endHourCondition: 0,
    endMinuteCondition: 0,
    imageRef: '',
    artworkX: 0,
    artworkY: 0,
    artworkWidth: 0,
    artworkHeight: 0,
    freeTextReference1X: 0,
    freeTextReference1Y: 0,
    freeTextReference1FontSize: 0,
    freeTextReference2X: 0,
    freeTextReference2Y: 0,
    freeTextReference2FontSize: 0,
  });

  const selectedAd = useMemo(
    () => advertisements.find((a) => a.id === selectedAdId) ?? null,
    [advertisements, selectedAdId]
  );
  const logoRefOptions = useMemo(() => {
    const current = String(layoutForm.displayLogoRef ?? '').trim();
    if (!current || LOGO_REF_DEFAULT_OPTIONS.includes(current)) {
      return LOGO_REF_DEFAULT_OPTIONS;
    }
    return [current, ...LOGO_REF_DEFAULT_OPTIONS];
  }, [layoutForm.displayLogoRef]);

  useEffect(() => {
    setIsEditingName(false);
    setEditingNameValue('');
  }, [selectedAdId]);

  const syncLayoutFormFromAd = useCallback((ad) => {
    if (!ad) return;
    setLayoutForm({
      displayLogo: toBool(ad.displayLogo),
      displayLogoRef: String(ad.displayLogoRef ?? ''),
      displayLogoX: toNum(ad.displayLogoX),
      displayLogoY: toNum(ad.displayLogoY),
      displayMediaBar: toBool(ad.displayMediaBar),
      displayMediaBarX: toNum(ad.displayMediaBarX),
      displayMediaBarY: toNum(ad.displayMediaBarY),
      displayErZijnNogCreditsCard: toBool(ad.displayErZijnNogCreditsCard),
      erZijnNogCreditsCardX: toNum(ad.erZijnNogCreditsCardX),
      erZijnNogCreditsCardY: toNum(ad.erZijnNogCreditsCardY),
      creditCondition: String(ad.creditCondition ?? 'NONE'),
      displayTimeInMilliseconds: toNum(ad.displayTimeInMilliseconds),
      active:
        ad.active === undefined || ad.active === null ? true : toBool(ad.active),
      priority: toNum(ad.priority),
      scheduledStartDate: toDateInputValue(ad.scheduledStartDate),
      startHourCondition: clamp(toNum(ad.startHourCondition), 0, 24),
      startMinuteCondition: clamp(toNum(ad.startMinuteCondition), 0, 60),
      endHourCondition: clamp(toNum(ad.endHourCondition), 0, 24),
      endMinuteCondition: clamp(toNum(ad.endMinuteCondition), 0, 60),
      imageRef: String(ad.imageRef ?? ''),
      artworkX: toNum(ad.artworkX),
      artworkY: toNum(ad.artworkY),
      artworkWidth: toNum(ad.artworkWidth),
      artworkHeight: toNum(ad.artworkHeight),
      freeTextReference1X: toNum(ad.freeTextReference1X),
      freeTextReference1Y: toNum(ad.freeTextReference1Y),
      freeTextReference1FontSize: toNum(ad.freeTextReference1FontSize),
      freeTextReference2X: toNum(ad.freeTextReference2X),
      freeTextReference2Y: toNum(ad.freeTextReference2Y),
      freeTextReference2FontSize: toNum(ad.freeTextReference2FontSize),
    });
  }, []);

  useEffect(() => {
    if (selectedAd) {
      syncLayoutFormFromAd(selectedAd);
    }
  }, [selectedAd, syncLayoutFormFromAd]);

  const filteredAds = useMemo(() => {
    if (!adSearch.trim()) return advertisements;
    const q = adSearch.toLowerCase().trim();
    return advertisements.filter((ad) => ad.name?.toLowerCase().includes(q));
  }, [advertisements, adSearch]);

  // Create flow re-homed from the Ads & Screensaver Subscriptions section
  // (DJB-89): the editor creates content, subscriptions only subscribe.
  const handleStartCreate = () => {
    setIsCreating(true);
    setSelectedAdId(null);
    setNewAdName('');
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setNewAdName('');
  };

  const handleSaveNewAd = async () => {
    const name = newAdName?.trim();
    if (!name) return;
    setCreating(true);
    try {
      const created = await createAdvertisement(name);
      if (created?.id) {
        setSelectedAdId(created.id);
      }
      setIsCreating(false);
      setNewAdName('');
    } catch (e) {
      // notification already shown in context
    } finally {
      setCreating(false);
    }
  };

  const startEditingName = () => {
    if (selectedAd) {
      setEditingNameValue(selectedAd.name ?? '');
      setIsEditingName(true);
    }
  };

  const cancelEditingName = () => {
    setIsEditingName(false);
    setEditingNameValue('');
  };

  const saveEditingName = async () => {
    if (!selectedAd) return;
    const trimmed = (editingNameValue ?? '').trim();
    if (trimmed === (selectedAd.name ?? '').trim()) {
      cancelEditingName();
      return;
    }
    try {
      await updateAdvertisement({ ...selectedAd, name: trimmed });
      setIsEditingName(false);
      setEditingNameValue('');
    } catch {
      // error already shown in context
    }
  };

  // DJB-233: the picker reads the ads pool itself (GET /image-library/ads)
  // each time it opens, and keeps a fresh upload visible right away.
  const openImagePicker = () => setImagePickerOpen(true);

  const handleImageSelect = async (resource) => {
    if (!selectedAd) return;
    try {
      await updateAdvertisement({ ...selectedAd, imageRef: resource });
    } catch {
      // error already shown in context
    }
  };

  // Resolves with the URL the server answered; the picker derives the file
  // name from it and shows the new image without a close/reopen.
  const handleImageUpload = (file) => uploadAdvertisementImage(file);

  const handleSaveLayout = async () => {
    if (!selectedAd) return;
    setLayoutSaving(true);
    try {
      await updateAdvertisement({
        ...selectedAd,
        displayLogo: layoutForm.displayLogo,
        displayLogoRef: layoutForm.displayLogoRef,
        displayLogoX: layoutForm.displayLogoX,
        displayLogoY: layoutForm.displayLogoY,
        displayMediaBar: layoutForm.displayMediaBar,
        displayMediaBarX: layoutForm.displayMediaBarX,
        displayMediaBarY: layoutForm.displayMediaBarY,
        displayErZijnNogCreditsCard: layoutForm.displayErZijnNogCreditsCard,
        erZijnNogCreditsCardX: layoutForm.erZijnNogCreditsCardX,
        erZijnNogCreditsCardY: layoutForm.erZijnNogCreditsCardY,
        creditCondition: layoutForm.creditCondition,
        displayTimeInMilliseconds: layoutForm.displayTimeInMilliseconds,
        active: layoutForm.active,
        priority: layoutForm.priority,
        scheduledStartDate: layoutForm.scheduledStartDate,
        startHourCondition: layoutForm.startHourCondition,
        startMinuteCondition: layoutForm.startMinuteCondition,
        endHourCondition: layoutForm.endHourCondition,
        endMinuteCondition: layoutForm.endMinuteCondition,
        imageRef: layoutForm.imageRef,
        artworkX: layoutForm.artworkX,
        artworkY: layoutForm.artworkY,
        artworkWidth: layoutForm.artworkWidth,
        artworkHeight: layoutForm.artworkHeight,
        freeTextReference1X: layoutForm.freeTextReference1X,
        freeTextReference1Y: layoutForm.freeTextReference1Y,
        freeTextReference1FontSize: layoutForm.freeTextReference1FontSize,
        freeTextReference2X: layoutForm.freeTextReference2X,
        freeTextReference2Y: layoutForm.freeTextReference2Y,
        freeTextReference2FontSize: layoutForm.freeTextReference2FontSize,
      });
    } catch {
      // error already shown in context
    } finally {
      setLayoutSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="assign-loading">
        <h1>Design</h1>
        <p>Loading data, please wait...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="assign-loading">
        <h1>Design</h1>
        <p>Error loading data: {error}</p>
      </div>
    );
  }

  return (
    <div className="bl-blocklist-manager design-page">
      <div className="bl-columns">
        <div className="bl-column">
          <div className="bl-machine-search-container design-search-row">
            <button
              type="button"
              className="design-btn design-btn-create"
              onClick={handleStartCreate}
            >
              Create
            </button>
            <input
              type="text"
              className="bl-machine-search"
              placeholder="Search by ad name..."
              value={adSearch}
              onChange={(e) => setAdSearch(e.target.value)}
            />
          </div>
          <div className="bl-machine-list-scrollable">
            <ul className="bl-machine-list">
              {!loading && !error && advertisements.length === 0 && (
                <li style={{ color: 'var(--theme-text-faint)' }}>No ads</li>
              )}
              {!loading && !error && filteredAds.length === 0 && advertisements.length > 0 && (
                <li style={{ color: 'var(--theme-text-faint)' }}>No matching ads</li>
              )}
              {!loading && !error && filteredAds.map((ad) => {
                const subCount = ad.subscribedMachines?.length ?? 0;
                return (
                  <li
                    key={ad.id}
                    className={`bl-machine-list-item${selectedAdId === ad.id ? ' selected' : ''}`}
                    onClick={() => {
                      setSelectedAdId(ad.id);
                      setIsCreating(false);
                    }}
                  >
                    <div className="bl-machine-row">
                      <span className="bl-machine-name">{ad.name}</span>
                      <span className="bl-blocklist-count">{subCount} subs</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
        <div className="bl-column">
          {isCreating ? (
            <div className="bl-selected-machine-details design-create-form">
              <div className="bl-selected-machine-title">Create advertisement</div>
              <label className="design-create-label" htmlFor="new-ad-name">
                Name
              </label>
              <input
                id="new-ad-name"
                type="text"
                className="design-name-input"
                placeholder="Ad name"
                value={newAdName}
                onChange={(e) => setNewAdName(e.target.value)}
                autoFocus
              />
              <div className="design-title-actions">
                <button
                  type="button"
                  className="design-btn design-btn-save"
                  onClick={handleSaveNewAd}
                  disabled={!newAdName?.trim() || creating}
                >
                  {creating ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  className="design-btn design-btn-cancel"
                  onClick={handleCancelCreate}
                  disabled={creating}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : selectedAd ? (
            <div className="bl-selected-machine-details">
              <div className="design-title-row">
                {isEditingName ? (
                  <>
                    <input
                      type="text"
                      className="design-name-input"
                      value={editingNameValue}
                      onChange={(e) => setEditingNameValue(e.target.value)}
                      autoFocus
                    />
                    <div className="design-title-actions">
                      <button type="button" className="design-btn design-btn-save" onClick={saveEditingName}>
                        Save
                      </button>
                      <button type="button" className="design-btn design-btn-cancel" onClick={cancelEditingName}>
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bl-selected-machine-title">{selectedAd.name}</div>
                    <button type="button" className="design-btn design-btn-edit" onClick={startEditingName}>
                      Edit
                    </button>
                  </>
                )}
              </div>
              <div className="bl-selected-machine-id">
                Ad id: <span>{selectedAd.id}</span>
              </div>
              <div className="design-ad-image-wrap">
                {selectedAd.imageRef ? (
                  <img
                    src={`https://tdj-s3.s3.eu-west-2.amazonaws.com//advertenties/${selectedAd.imageRef}`}
                    alt={selectedAd.name}
                    className="design-ad-image"
                  />
                ) : (
                  <div className="design-ad-image-placeholder">No image</div>
                )}
              </div>
              <div className="design-ad-choose-wrap">
                <span
                  role="button"
                  tabIndex={0}
                  className="design-ad-choose-image"
                  onClick={openImagePicker}
                  onKeyDown={(e) => e.key === 'Enter' && openImagePicker()}
                >
                  Choose image
                </span>
              </div>
            </div>
          ) : (
            <div className="bl-no-selection-placeholder">
              Select an ad to see details
            </div>
          )}
        </div>
        <div className="bl-column design-column">
          {selectedAd ? (
            <div className="design-layout-panel">
              <div className="design-layout-scroll">
                <section className="design-layout-section" aria-labelledby="design-section-spinner">
                  <h3 id="design-section-spinner" className="design-section-title">
                    Display Spinner
                  </h3>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-displayLogo">
                      Display logo
                    </label>
                    <select
                      id="layout-displayLogo"
                      className="design-field-control design-field-select"
                      value={layoutForm.displayLogo ? 'true' : 'false'}
                      onChange={(e) =>
                        setLayoutForm((f) => ({ ...f, displayLogo: e.target.value === 'true' }))
                      }
                    >
                      <option value="true">true</option>
                      <option value="false">false</option>
                    </select>
                  </div>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-displayLogoRef">
                      Logo Ref.
                    </label>
                    <select
                      id="layout-displayLogoRef"
                      className="design-field-control design-field-select"
                      value={layoutForm.displayLogoRef}
                      onChange={(e) =>
                        setLayoutForm((f) => ({ ...f, displayLogoRef: e.target.value }))
                      }
                    >
                      {logoRefOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-displayLogoX">
                      Position X
                    </label>
                    <input
                      id="layout-displayLogoX"
                      type="number"
                      className="design-field-control design-field-input design-field-input-num"
                      value={layoutForm.displayLogoX}
                      onChange={(e) =>
                        setLayoutForm((f) => ({
                          ...f,
                          displayLogoX: toNum(e.target.value),
                        }))
                      }
                    />
                  </div>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-displayLogoY">
                      Position Y
                    </label>
                    <input
                      id="layout-displayLogoY"
                      type="number"
                      className="design-field-control design-field-input design-field-input-num"
                      value={layoutForm.displayLogoY}
                      onChange={(e) =>
                        setLayoutForm((f) => ({
                          ...f,
                          displayLogoY: toNum(e.target.value),
                        }))
                      }
                    />
                  </div>
                </section>
                <section className="design-layout-section" aria-labelledby="design-section-mediabar">
                  <h3 id="design-section-mediabar" className="design-section-title">
                    Display Mediabar
                  </h3>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-displayMediaBar">
                      Display mediabar
                    </label>
                    <select
                      id="layout-displayMediaBar"
                      className="design-field-control design-field-select"
                      value={layoutForm.displayMediaBar ? 'true' : 'false'}
                      onChange={(e) =>
                        setLayoutForm((f) => ({ ...f, displayMediaBar: e.target.value === 'true' }))
                      }
                    >
                      <option value="false">false</option>
                      <option value="true">true</option>
                    </select>
                  </div>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-displayMediaBarX">
                      Position X
                    </label>
                    <input
                      id="layout-displayMediaBarX"
                      type="number"
                      className="design-field-control design-field-input design-field-input-num"
                      value={layoutForm.displayMediaBarX}
                      onChange={(e) =>
                        setLayoutForm((f) => ({
                          ...f,
                          displayMediaBarX: toNum(e.target.value),
                        }))
                      }
                    />
                  </div>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-displayMediaBarY">
                      Position Y
                    </label>
                    <input
                      id="layout-displayMediaBarY"
                      type="number"
                      className="design-field-control design-field-input design-field-input-num"
                      value={layoutForm.displayMediaBarY}
                      onChange={(e) =>
                        setLayoutForm((f) => ({
                          ...f,
                          displayMediaBarY: toNum(e.target.value),
                        }))
                      }
                    />
                  </div>
                </section>
                <section className="design-layout-section" aria-labelledby="design-section-credit">
                  <h3 id="design-section-credit" className="design-section-title">
                    Credit Condition
                  </h3>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-creditCondition">
                      Condition
                    </label>
                    <select
                      id="layout-creditCondition"
                      className="design-field-control design-field-select"
                      value={layoutForm.creditCondition}
                      onChange={(e) =>
                        setLayoutForm((f) => ({ ...f, creditCondition: e.target.value }))
                      }
                    >
                      {CREDIT_CONDITION_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-displayErZijnNogCreditsCard">
                      Display Credit Badge
                    </label>
                    <select
                      id="layout-displayErZijnNogCreditsCard"
                      className="design-field-control design-field-select"
                      value={layoutForm.displayErZijnNogCreditsCard ? 'true' : 'false'}
                      onChange={(e) =>
                        setLayoutForm((f) => ({
                          ...f,
                          displayErZijnNogCreditsCard: e.target.value === 'true',
                        }))
                      }
                    >
                      <option value="true">true</option>
                      <option value="false">false</option>
                    </select>
                  </div>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-erZijnNogCreditsCardX">
                      Badge X
                    </label>
                    <input
                      id="layout-erZijnNogCreditsCardX"
                      type="number"
                      className="design-field-control design-field-input design-field-input-num"
                      value={layoutForm.erZijnNogCreditsCardX}
                      onChange={(e) =>
                        setLayoutForm((f) => ({
                          ...f,
                          erZijnNogCreditsCardX: toNum(e.target.value),
                        }))
                      }
                    />
                  </div>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-erZijnNogCreditsCardY">
                      Badge Y
                    </label>
                    <input
                      id="layout-erZijnNogCreditsCardY"
                      type="number"
                      className="design-field-control design-field-input design-field-input-num"
                      value={layoutForm.erZijnNogCreditsCardY}
                      onChange={(e) =>
                        setLayoutForm((f) => ({
                          ...f,
                          erZijnNogCreditsCardY: toNum(e.target.value),
                        }))
                      }
                    />
                  </div>
                </section>
                <section className="design-layout-section" aria-labelledby="design-section-duration">
                  <h3 id="design-section-duration" className="design-section-title">
                    Display Duration
                  </h3>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-displayTimeInMilliseconds">
                      Display duration
                    </label>
                    <div className="design-field-control design-field-with-unit">
                      <input
                        id="layout-displayTimeInMilliseconds"
                        type="number"
                        className="design-field-input design-field-input-num"
                        value={layoutForm.displayTimeInMilliseconds}
                        onChange={(e) =>
                          setLayoutForm((f) => ({
                            ...f,
                            displayTimeInMilliseconds: toNum(e.target.value),
                          }))
                        }
                      />
                      <span className="design-field-unit-suffix" aria-hidden>
                        ms
                      </span>
                    </div>
                  </div>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-active">
                      active
                    </label>
                    <select
                      id="layout-active"
                      className="design-field-control design-field-select"
                      value={layoutForm.active ? 'true' : 'false'}
                      onChange={(e) =>
                        setLayoutForm((f) => ({ ...f, active: e.target.value === 'true' }))
                      }
                    >
                      <option value="true">true</option>
                      <option value="false">false</option>
                    </select>
                  </div>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-priority">
                      Priority
                    </label>
                    <input
                      id="layout-priority"
                      type="number"
                      className="design-field-control design-field-input design-field-input-num"
                      value={layoutForm.priority}
                      onChange={(e) =>
                        setLayoutForm((f) => ({
                          ...f,
                          priority: toNum(e.target.value),
                        }))
                      }
                    />
                  </div>
                </section>
                <section className="design-layout-section" aria-labelledby="design-section-visibility">
                  <h3 id="design-section-visibility" className="design-section-title">
                    Visibility
                  </h3>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-scheduledStartDate">
                      Launch Date
                    </label>
                    <input
                      id="layout-scheduledStartDate"
                      type="date"
                      className="design-field-control design-field-input design-field-input-date"
                      value={layoutForm.scheduledStartDate}
                      onChange={(e) =>
                        setLayoutForm((f) => ({ ...f, scheduledStartDate: e.target.value }))
                      }
                    />
                  </div>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-startHourCondition">
                      Start hour
                    </label>
                    <input
                      id="layout-startHourCondition"
                      type="number"
                      min={0}
                      max={24}
                      className="design-field-control design-field-input design-field-input-num"
                      value={layoutForm.startHourCondition}
                      onChange={(e) =>
                        setLayoutForm((f) => ({
                          ...f,
                          startHourCondition: clamp(toNum(e.target.value), 0, 24),
                        }))
                      }
                    />
                  </div>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-startMinuteCondition">
                      Start minute
                    </label>
                    <input
                      id="layout-startMinuteCondition"
                      type="number"
                      min={0}
                      max={60}
                      className="design-field-control design-field-input design-field-input-num"
                      value={layoutForm.startMinuteCondition}
                      onChange={(e) =>
                        setLayoutForm((f) => ({
                          ...f,
                          startMinuteCondition: clamp(toNum(e.target.value), 0, 60),
                        }))
                      }
                    />
                  </div>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-endHourCondition">
                      End hour
                    </label>
                    <input
                      id="layout-endHourCondition"
                      type="number"
                      min={0}
                      max={24}
                      className="design-field-control design-field-input design-field-input-num"
                      value={layoutForm.endHourCondition}
                      onChange={(e) =>
                        setLayoutForm((f) => ({
                          ...f,
                          endHourCondition: clamp(toNum(e.target.value), 0, 24),
                        }))
                      }
                    />
                  </div>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-endMinuteCondition">
                      End minute
                    </label>
                    <input
                      id="layout-endMinuteCondition"
                      type="number"
                      min={0}
                      max={60}
                      className="design-field-control design-field-input design-field-input-num"
                      value={layoutForm.endMinuteCondition}
                      onChange={(e) =>
                        setLayoutForm((f) => ({
                          ...f,
                          endMinuteCondition: clamp(toNum(e.target.value), 0, 60),
                        }))
                      }
                    />
                  </div>
                </section>
                <section className="design-layout-section" aria-labelledby="design-section-artwork">
                  <h3 id="design-section-artwork" className="design-section-title">
                    Display Artwork
                  </h3>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-imageRef">
                      Artwork ref.
                    </label>
                    <input
                      id="layout-imageRef"
                      type="text"
                      className="design-field-control design-field-input"
                      value={layoutForm.imageRef}
                      disabled
                    />
                  </div>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-artworkX">
                      Position X
                    </label>
                    <input
                      id="layout-artworkX"
                      type="number"
                      className="design-field-control design-field-input design-field-input-num"
                      value={layoutForm.artworkX}
                      onChange={(e) =>
                        setLayoutForm((f) => ({ ...f, artworkX: toNum(e.target.value) }))
                      }
                    />
                  </div>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-artworkY">
                      Position Y
                    </label>
                    <input
                      id="layout-artworkY"
                      type="number"
                      className="design-field-control design-field-input design-field-input-num"
                      value={layoutForm.artworkY}
                      onChange={(e) =>
                        setLayoutForm((f) => ({ ...f, artworkY: toNum(e.target.value) }))
                      }
                    />
                  </div>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-artworkWidth">
                      Width
                    </label>
                    <input
                      id="layout-artworkWidth"
                      type="number"
                      className="design-field-control design-field-input design-field-input-num"
                      value={layoutForm.artworkWidth}
                      onChange={(e) =>
                        setLayoutForm((f) => ({ ...f, artworkWidth: toNum(e.target.value) }))
                      }
                    />
                  </div>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-artworkHeight">
                      Height
                    </label>
                    <input
                      id="layout-artworkHeight"
                      type="number"
                      className="design-field-control design-field-input design-field-input-num"
                      value={layoutForm.artworkHeight}
                      onChange={(e) =>
                        setLayoutForm((f) => ({ ...f, artworkHeight: toNum(e.target.value) }))
                      }
                    />
                  </div>
                </section>
                <section className="design-layout-section" aria-labelledby="design-section-freetext">
                  <h3 id="design-section-freetext" className="design-section-title">
                    Display Text
                  </h3>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-freeTextReference1X">
                      Text 1 Ref. X
                    </label>
                    <input
                      id="layout-freeTextReference1X"
                      type="number"
                      className="design-field-control design-field-input design-field-input-num"
                      value={layoutForm.freeTextReference1X}
                      onChange={(e) =>
                        setLayoutForm((f) => ({
                          ...f,
                          freeTextReference1X: toNum(e.target.value),
                        }))
                      }
                    />
                  </div>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-freeTextReference1Y">
                      Text 1 Ref. Y
                    </label>
                    <input
                      id="layout-freeTextReference1Y"
                      type="number"
                      className="design-field-control design-field-input design-field-input-num"
                      value={layoutForm.freeTextReference1Y}
                      onChange={(e) =>
                        setLayoutForm((f) => ({
                          ...f,
                          freeTextReference1Y: toNum(e.target.value),
                        }))
                      }
                    />
                  </div>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-freeTextReference1FontSize">
                      Text 1 Fontsize
                    </label>
                    <input
                      id="layout-freeTextReference1FontSize"
                      type="number"
                      className="design-field-control design-field-input design-field-input-num"
                      value={layoutForm.freeTextReference1FontSize}
                      onChange={(e) =>
                        setLayoutForm((f) => ({
                          ...f,
                          freeTextReference1FontSize: toNum(e.target.value),
                        }))
                      }
                    />
                  </div>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-freeTextReference2X">
                      Text 2 Ref. X
                    </label>
                    <input
                      id="layout-freeTextReference2X"
                      type="number"
                      className="design-field-control design-field-input design-field-input-num"
                      value={layoutForm.freeTextReference2X}
                      onChange={(e) =>
                        setLayoutForm((f) => ({
                          ...f,
                          freeTextReference2X: toNum(e.target.value),
                        }))
                      }
                    />
                  </div>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-freeTextReference2Y">
                      Text 2 Ref. Y
                    </label>
                    <input
                      id="layout-freeTextReference2Y"
                      type="number"
                      className="design-field-control design-field-input design-field-input-num"
                      value={layoutForm.freeTextReference2Y}
                      onChange={(e) =>
                        setLayoutForm((f) => ({
                          ...f,
                          freeTextReference2Y: toNum(e.target.value),
                        }))
                      }
                    />
                  </div>
                  <div className="design-field-row">
                    <label className="design-field-label" htmlFor="layout-freeTextReference2FontSize">
                      Text 2 Fontsize
                    </label>
                    <input
                      id="layout-freeTextReference2FontSize"
                      type="number"
                      className="design-field-control design-field-input design-field-input-num"
                      value={layoutForm.freeTextReference2FontSize}
                      onChange={(e) =>
                        setLayoutForm((f) => ({
                          ...f,
                          freeTextReference2FontSize: toNum(e.target.value),
                        }))
                      }
                    />
                  </div>
                </section>
              </div>
              <button
                type="button"
                className="design-layout-save"
                onClick={handleSaveLayout}
                disabled={layoutSaving}
              >
                {layoutSaving ? 'Saving…' : 'SAVE'}
              </button>
            </div>
          ) : (
            <div className="bl-no-selection-placeholder">
              Select an ad to design
            </div>
          )}
        </div>
      </div>
      <AdImagePicker
        isOpen={imagePickerOpen}
        onClose={() => setImagePickerOpen(false)}
        pool={IMAGE_POOLS.ADS}
        onSelect={handleImageSelect}
        onUpload={handleImageUpload}
      />
    </div>
  );
};

export default Designer;
